import React from 'react';
import { formatDate, DATE, WEEKDAY, type DateInput } from '@koob/dates';
import { getDayEffectiveEndPosition } from '@koob/utils';
import TripDayCard from '../TripDayCard';
import EmptyProgramCard from '../EmptyProgramCard';
import TripDayReadOnlyExperience from './TripDayReadOnlyExperience';
import TripDayReadOnlyHotel from './TripDayReadOnlyHotel';
import {
  getEmptyAccommodation,
  getVersionDayLocations,
} from '../../logic/getVersionDayCard';
import type { Translation } from './types';

/**
 * KD-3581 §3 — one day of an EARLIER version of the trip, in the shape of the
 * live day with every control removed. It prices itself (experience and
 * hotel) and adds those prices to the compare panel's total.
 *
 * `days` and `experiences` are the version's own: the arrival city of a day is
 * read from them, where a retained transfer can move it.
 */
export default function TripDayReadOnly({
  dayIndex,
  date,
  departureDate,
  days,
  experiences,
  experience,
  experienceDayIndex,
  extras,
  hotel,
  hotelBookedOnMyOwn,
  frame,
  stayRooms,
  t,
}: {
  dayIndex: number;
  date: DateInput;
  departureDate: DateInput;
  days: any[];
  experiences: Record<string, any>;
  experience: any;
  experienceDayIndex?: number;
  extras: any[];
  hotel: any;
  hotelBookedOnMyOwn?: boolean;
  frame: any;
  stayRooms: any;
  t: Translation;
}) {
  const program = experience?.programs?.[(experienceDayIndex ?? 1) - 1];

  const effectiveEndPosition = getDayEffectiveEndPosition({
    days,
    dayIndex,
    experiencesMap: experiences,
    experience,
    program,
    selectedExtras: extras,
  });
  const { startLocation, endLocation } = getVersionDayLocations({
    experience,
    effectiveEndPosition,
  });
  const emptyAccommodation = getEmptyAccommodation({
    program,
    hotelBookedOnMyOwn,
    t,
  });

  return (
    <TripDayCard
      dayLabel={`${t('compose.day')} ${dayIndex + 1}`}
      weekdayLabel={formatDate(date, WEEKDAY)}
      dateLabel={date ? formatDate(date, DATE) : '--/--/----'}
      program={
        experience ? (
          <TripDayReadOnlyExperience
            experience={experience}
            experienceDayIndex={experienceDayIndex}
            extras={extras}
            date={date}
            itineraryLabel={t('compose.frame.itinerary', {
              from: startLocation ?? '--',
              to: endLocation ?? startLocation ?? '--',
            })}
            departureDate={departureDate}
            frame={frame}
            t={t}
          />
        ) : (
          <EmptyProgramCard label={t('compose.dayCard.noProgram')} />
        )
      }
      accommodation={
        hotel ? (
          <TripDayReadOnlyHotel
            hotel={hotel}
            date={date}
            included={program?.isIncludedAccomodation}
            stayRooms={stayRooms}
            t={t}
          />
        ) : (
          <EmptyProgramCard
            label={emptyAccommodation.label}
            variant={emptyAccommodation.variant}
          />
        )
      }
    />
  );
}
